import { VariantServices } from "../../types/types";
import { useEffect } from "react";
import styles from "./DeliveryServices.module.scss";
import OptionList from "@JohnLewisPartnership/jl-design-system/dist/components/option/OptionList";
import { DeliveryServiceItem } from "./components/DeliveryServiceItem";
import ESIInclude from "@JohnLewisPartnership/jl-esi-components";
import {
  SERVICE_QUESTIONNAIRE_ESI_COMPLETE,
  SERVICE_QUESTIONNAIRE_ESI_SERVICE_SELECTED,
} from "../../constants/constants";
import {
  emitServices,
  removeDmsPrefix,
} from "../../utils/services/emitServices";
import { toggleSelectedService } from "../../utils/services/toggleSelectedService";
import { TransformedDeliveryService } from "../../types/serviceTypes";
import { mappedServices } from "../../utils/services/serviceMapper";
import { useSharedServices } from "../../utils/services/useSharedServices";

type Props = {
  /**
   * Indicates if a product variant is available to order.
   */
  availableToOrder: boolean;
  /**
   * The services available for a product variant.
   */
  variantServices: VariantServices;
  /**
   * Feature flag enabling the delivery service questionnaire for complex installs
   */
  deliveryServiceQuestionnaireEnabled: boolean;
  /**
   * Feature flag enabling for testing use of free promo tag on free services
   */
  showFreeServicePromoTag: boolean;
};

type ServiceQuestionnaireRequest = {
  dmsId: string;
  serviceId: string;
  parentItemId: string;
};

type ServiceQuestionnaireResult = {
  serviceId: string;
  success: boolean;
};

type GasCheckServiceQuestionnaireRequest = {
  services: [
    {
      dmsId: string;
      serviceId: string;
    },
    {
      dmsId: string;
      serviceId: string;
    },
  ];
  parentItemId: string;
};

export const DeliveryServicesUI = ({
  availableToOrder,
  variantServices,
  deliveryServiceQuestionnaireEnabled,
  showFreeServicePromoTag,
}: Props) => {
  const [services, updateServices] = useSharedServices([]);

  useEffect(() => {
    if (services.length > 0) {
      resetSelectedServices();
    }
    updateServices(mappedServices(variantServices));
  }, [variantServices]);

  const deliveryServices = services.filter(
    (service) => service.serviceType === "DeliveryService",
  ) as TransformedDeliveryService[];

  const resetSelectedServices = () => {
    updateServices((prevState) =>
      prevState.map((service) => {
        return { ...service, selected: false };
      }),
    );
    emitServices([]);
  };

  const onToggle = (service: TransformedDeliveryService) => {
    const { id, skuId, selected } = service;
    if (deliveryServiceQuestionnaireEnabled && !selected) {
      openServiceQuestionnaire(skuId, id);
    } else {
      updateServices(toggleSelectedService(id, services));
    }
  };

  const handleDeliveryServiceQuestionnaireCompletion = (
    event: CustomEvent<ServiceQuestionnaireResult>,
  ) => {
    const { serviceId, success } = event.detail;
    if (success) {
      updateServices(toggleSelectedService(serviceId, services));
    }
  };

  useEffect(() => {
    window.addEventListener("beforeunload", resetSelectedServices);

    if (deliveryServiceQuestionnaireEnabled) {
      window.addEventListener(
        SERVICE_QUESTIONNAIRE_ESI_COMPLETE,
        handleDeliveryServiceQuestionnaireCompletion,
      );
    }

    return () => {
      window.removeEventListener("beforeunload", resetSelectedServices);
      window.removeEventListener(
        SERVICE_QUESTIONNAIRE_ESI_COMPLETE,
        handleDeliveryServiceQuestionnaireCompletion,
      );
    };
  }, [resetSelectedServices, handleDeliveryServiceQuestionnaireCompletion]);

  if (!deliveryServices.length) return null;

  return (
    <div data-testid="product:services-and-warranties">
      <div data-testid="product:delivery-services" className={styles.services}>
        <h2 className={styles.title}>Installation & Disposal</h2>
        <OptionList isButtonList={false} isRow={false}>
          {deliveryServices.map((service, index) => {
            return (
              <DeliveryServiceItem
                checked={service.selected}
                title={service.title}
                key={`service-${index}`}
                serviceId={service.id}
                description={service.description}
                display={service.display}
                value={service.value}
                toggleCheck={() => onToggle(service)}
                disabled={!availableToOrder}
                showFreeServicePromoTag={showFreeServicePromoTag}
              />
            );
          })}
        </OptionList>
        {deliveryServiceQuestionnaireEnabled && (
          <div data-testid={"service-questionnaire-esi-wrapper"}>
            <ESIInclude
              src={"/service-questionnaire/index.html"}
              id={"service-questionnaire"}
            />
          </div>
        )}
      </div>
    </div>
  );
};

function buildPayload(dmsId: string, serviceId: string) {
  const nonGasPayload: ServiceQuestionnaireRequest = {
    dmsId: dmsId,
    serviceId: serviceId,
    parentItemId: "not-required-for-pdp",
  };
  return nonGasPayload;
}

function buildGasCheckPayload(dmsId: string, serviceId: string) {
  const gasPayload: GasCheckServiceQuestionnaireRequest = {
    services: [
      {
        dmsId: dmsId,
        serviceId: serviceId,
      },
      {
        dmsId: dmsId,
        serviceId: serviceId,
      },
    ],
    parentItemId: "not-required-for-pdp",
  };
  return gasPayload;
}

function openServiceQuestionnaire(skuId: string, serviceId: string) {
  const dmsId = removeDmsPrefix(skuId);
  const checkForGas = ["3913", "6901", "3905", "8301", "3911", "3908"];

  let payload;
  if (checkForGas.includes(dmsId)) {
    payload = buildGasCheckPayload(dmsId, serviceId);
  } else {
    payload = buildPayload(dmsId, serviceId);
  }

  window.dispatchEvent(
    new CustomEvent(SERVICE_QUESTIONNAIRE_ESI_SERVICE_SELECTED, {
      detail: payload,
    }),
  );
}
